import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { ROUTES } from "../../../routes";
import { storeInstructorAccessToken } from "../../authentication/instructorToken";
import userManager from "../../authorization/authService";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { PortalSessionResponse } from "../../portal/contracts/PortalSessionResponse";
import { SwitchPortalContextRequest } from "../../portal/contracts/SwitchPortalContextRequest";
import portalAxios, { StoredPortalSession, storePortalSession } from "../../portal/portalAxios";
import { portalSessionOpened } from "../../portal/portal-slice";
import { toStoredPortalSession } from "../../portal/slice-extensions/portal-slice-extension";
import { ContextsResponse } from "../contracts/ContextsResponse";
import { ContextTokenResponse } from "../contracts/ContextTokenResponse";
import { ContextKind } from "../enums/ContextKind";
import { ContextResponseMapper, firstNameOf } from "../mappers/ContextResponseMapper";
import { Context, ContextList } from "../models/Context";
import { reloadAt } from "../utils/contextNavigation";

const mapper = new ContextResponseMapper();

/** The slice of the root state the switch reads: the person's contexts, for the portal rows. */
interface ContextsHolder {
  contexts: { contexts: Context[] };
}

/** The signed-in person's contexts across organizations (`GET me/contexts`). */
export const fetchContexts = createAsyncThunk<ContextList, void, { rejectValue: ApiErrorResponse }>(
  "contexts/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<ContextsResponse>("/me/contexts");
      return mapper.toContextList(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/**
 * Switches to another context: the server issues its token and remembers it. An instructor token
 * replaces the stored sign-in and the app reloads at home; a member token becomes the portal session
 * and the portal opens for that member. The reload resets every store.
 */
export const switchContext = createAsyncThunk<void, Context, { rejectValue: ApiErrorResponse }>(
  "contexts/switch",
  async (context, { getState, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ContextTokenResponse>(
        `/me/contexts/${encodeURIComponent(context.key)}/token`,
      );
      const token = response.data;
      if (token.kind === ContextKind.Instructor) {
        // The stored JWT now speaks for the person; an OIDC user left behind would sign the old context back in
        // after logout. Removed first, so a failure here leaves the current sign-in intact.
        await userManager.removeUser();
        storeInstructorAccessToken(token.accessToken);
        reloadAt(ROUTES.HOME);
        return;
      }
      const memberId = context.memberId as string;
      storeMemberToken(context, token, (getState() as ContextsHolder).contexts.contexts);
      reloadAt(`${ROUTES.PORTAL}?member=${encodeURIComponent(memberId)}`);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/** A member context's token becomes the portal session, with every member row of the person as a context. */
const storeMemberToken = (context: Context, token: ContextTokenResponse, all: Context[]): StoredPortalSession => {
  const session: StoredPortalSession = {
    memberId: context.memberId as string,
    firstName: firstNameOf(context.displayName),
    expiresAtUtc: token.expiresAtUtc,
    contexts: mapper.toPortalContexts(all),
  };
  storePortalSession(token.accessToken, session);
  return session;
};

/**
 * Opens a member context of a sign-in without reloading (US-113, PD-0056): a member-only password, code or
 * provider sign-in lands on the portal. The token from `me/contexts/{key}/token` becomes the portal session,
 * which the portal slice takes over (`portalSessionOpened`).
 */
export const openMemberContext = createAsyncThunk<StoredPortalSession, Context, { rejectValue: ApiErrorResponse }>(
  "contexts/openMember",
  async (context, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ContextTokenResponse>(
        `/me/contexts/${encodeURIComponent(context.key)}/token`,
      );
      const all = (getState() as ContextsHolder).contexts?.contexts ?? [];
      const session = storeMemberToken(context, response.data, all.length > 0 ? all : [context]);
      dispatch(portalSessionOpened(session));
      return session;
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/**
 * Switches the portal session to another member row of the same email (`POST portal/auth/switch`)
 * and reloads at `continueTo` (the portal home by default).
 */
export const switchPortalContext = createAsyncThunk<
  void,
  { memberId: string; continueTo?: string },
  { rejectValue: ApiErrorResponse }
>("contexts/switchPortal", async ({ memberId, continueTo }, { rejectWithValue }) => {
  try {
    const request: SwitchPortalContextRequest = { memberId };
    const response = await portalAxios.post<PortalSessionResponse>("/portal/auth/switch", request);
    storePortalSession(response.data.accessToken, toStoredPortalSession(response.data));
    reloadAt(continueTo ?? ROUTES.PORTAL);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
